import { ChevronDown } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useController, UseControllerProps } from 'react-hook-form'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

interface QuickCheckBoxFilterProps {
  options: { value: string; label: string }[]
  label: string
  icon?: React.ReactNode
  onChange: (_value: { displayName: string; value: string }[]) => void
  onFormSubmit?: () => void
}

export const QuickCheckBoxFilter = (
  props: QuickCheckBoxFilterProps & UseControllerProps
) => {
  const { options, onChange, label, icon, defaultValue, onFormSubmit } = props
  const { field } = useController(props)
  const [activeStatuses, setActiveStatuses] = useState<string[]>(
    defaultValue ?? []
  )

  useEffect(() => {
    options.unshift({ value: 'all', label: 'Todos' })
    field.value = defaultValue ?? []
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Efecto para sincronizar con cambios externos del formulario
  useEffect(() => {
    if (field.value && Array.isArray(field.value)) {
      setActiveStatuses(field.value)
    } else if (!field.value || field.value.length === 0) {
      setActiveStatuses([])
    }
  }, [field.value])
  const toggleStatus = (value: string) => {
    if (value === 'all') {
      if (activeStatuses.includes('all')) {
        setActiveStatuses([])
        field.onChange([])
        onChange([
          {
            displayName: label,
            value: '',
          },
        ])
      } else {
        const allValues = options.map((s) => s.value)
        const allLabels = options.map((s) => s.label)
        setActiveStatuses(allValues)
        field.onChange(allValues)
        onChange([
          {
            displayName: label,
            value: allLabels.join(', '),
          },
        ])
      }

      // Call form submit if provided
      if (onFormSubmit) {
        onFormSubmit()
      }
      return
    }

    // Remove "all" if it's in the array
    let newStatuses = activeStatuses.filter((s) => s.toLowerCase() !== 'all')

    // Toggle the selected status
    if (newStatuses.includes(value)) {
      newStatuses = newStatuses.filter((s) => s !== value)
      // If no statuses left, set back to "all"
      if (newStatuses.length === 0) {
        newStatuses = []
      }
    } else {
      newStatuses.push(value)
    }

    setActiveStatuses(newStatuses)
    const displayOptions = newStatuses.map(
      (itm) => options.find((item) => item.value === itm)?.label
    )
    onChange([
      {
        displayName: label,
        value: displayOptions.join(', '),
      },
    ])
    // Set field.value to the actual values (not labels) of selected options
    field.onChange(newStatuses)

    // Call form submit if provided
    if (onFormSubmit) {
      onFormSubmit()
    }
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          aria-label="Seleccionar estado"
          className="gap-1.5"
          variant="outline"
        >
          {icon}
          <span>{label}</span>
          <ChevronDown className="h-4 w-4 opacity-50" />
          {activeStatuses.length > 0 && activeStatuses[0] !== 'all' && (
            <Badge className="ml-1 rounded-full px-1.5" variant="secondary">
              {activeStatuses.length}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[200px] p-0">
        <Command>
          <CommandInput placeholder={`Buscar ${label.toLowerCase()}...`} />
          <CommandList>
            <CommandEmpty>No se encontraron resultados.</CommandEmpty>
            <CommandGroup>
              {options.map((status) => (
                <CommandItem
                  className="flex items-center gap-2"
                  key={status.value}
                  onSelect={() => toggleStatus(status.value)}
                >
                  <div className="flex flex-1 items-center gap-2">
                    <Checkbox
                      checked={activeStatuses.includes(status.value)}
                      onCheckedChange={() => toggleStatus(status.value)}
                    />
                    <span>{status.label}</span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
